import dayjs from "dayjs";

export function formatBytesBinary(bytes?: number) {
    if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
        return "n/a";
    }

    const units = ["B", "KiB", "MiB", "GiB", "TiB"];
    const base = 1024;

    let value = bytes;
    let unitIndex = 0;

    while (value >= base && unitIndex < units.length - 1) {
        value /= base;
        unitIndex++;
    }

    if (unitIndex === 0) {
        return `${bytes} B`;
    }

    const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toFixed(decimals)} ${units[unitIndex]}`;
}

export function utf8ByteLength(value: any): number {
    if (value === undefined || value === null) {
        return 0;
    }

    if (typeof value === "string") {
        return new TextEncoder().encode(value).length;
    }

    return new TextEncoder().encode(JSON.stringify(value)).length;
}

export function formatDateTime(date?: Date) {
    if (date === undefined || date === null) {
        return <i>never</i>;
    }

    return dayjs(date).format("YYYY-MM-DD HH:mm:ss")
}

export function formatDate(date?: Date) {
    if (date === undefined || date === null) {
        return <i>never</i>;
    }

    return dayjs(date).format("YYYY-MM-DD")
}

export function capitalize(text: string) {
    return text[0].toUpperCase() + text.slice(1);
}

export function uncapitalize(text: string) {
    return text[0].toLowerCase() + text.slice(1);
}

export function formatDuration(durationInSeconds?: number) {
    if (durationInSeconds === undefined) {
        return;
    }

    const duration = dayjs.duration(durationInSeconds, 'seconds')

    return duration.humanize(false);
}

export function removeTrailingSlash(text?: string) {
    if (text && text.charAt(text.length - 1) === '/') {
        return text.substring(0, text.length - 1);
    } else {
        return text;
    }
}

export function filterObject(o: Object, exclude: string[]) {
    const filteredObject = {};
    Object.keys(o).forEach(key => {
        if (exclude.indexOf(key) === -1) {
            filteredObject[key] = o[key];
        }
    });

    return filteredObject;
}

export function formatCreditsFixed(value?: number, decimals: number = 4): string {
    return typeof value === "number" ? `${value.toFixed(decimals)} credits` : "-";
}

export function formatCredits(value?: number): string {
    if (typeof value !== "number") return "-";
    if (!value) return "0.00 credits";

    if (Math.abs(value) < 0.01) {
        return new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 4,
            maximumFractionDigits: 6
        }).format(value) + " credits";
    }

    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value) + " credits";
}

export function formatCreditsCompact(value?: number): string {
    if (typeof value !== "number") return "-";
    if (!value) return "0 credits";

    if (Math.abs(value) < 0.01) {
        return new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6
        }).format(value) + " credits";
    }

    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(value) + " credits";
}

export function formatInteger(value: number) {
    return new Intl.NumberFormat().format(value || 0);
}

export function formatCompactInteger(value: number): string {
    if (!value) return "0";
    if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "k";

    return new Intl.NumberFormat().format(value);
}

export function formatDurationMs(ms: number): string {
    if (!ms) return "0 ms";
    if (ms < 1_000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}s`;
    if (ms < 3_600_000) {
        const minutes = Math.floor(ms / 60_000);
        const seconds = Math.floor((ms % 60_000) / 1_000);

        return `${minutes}m ${seconds}s`;
    }

    const hours = Math.floor(ms / 3_600_000);
    const minutes = Math.floor((ms % 3_600_000) / 60_000);

    return `${hours}h ${minutes}m`;
}

export function syntaxHighlight(json) {
    if (typeof json != 'string') {
        json = JSON.stringify(json, undefined, 2);
    }

    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match) {
        let cls = 'number';

        if (/^"/.test(match)) {
            if (/:$/.test(match)) {
                cls = 'key';
            } else {
                cls = 'string';
            }
        } else if (/true|false/.test(match)) {
            cls = 'boolean';
        } else if (/null/.test(match)) {
            cls = 'null';
        }

        return '<span class="' + cls + '">' + match + '</span>';
    });
}
